import { Component, Inject, OnInit } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef } from '@angular/material/dialog'
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { NgClass } from '@angular/common'
import { AutoCompleteModule } from 'primeng/autocomplete'
import { ProductService } from '../product-list/product.service'
import { SpinnerOverlayService } from '../../../Components/spinner/spinner-overlay.service'
import { CustomerService } from '../../../shared/service/customer-service';
import { RoleService } from '../../../shared/service/role-service';
import { IImage, IProduct } from '../../../shared/model/product.model';
import { FileUpload } from 'primeng/fileupload';
import { PrimeNG } from 'primeng/config';
import { MessageService } from '../../../shared/message.service';
import { Button } from '../../../Components/button/button'

@Component({
  selector: 'app-product-update',
  imports: [
    MatDialogContent,
    MatDialogActions,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    AutoCompleteModule,
    FileUpload,
    Button,
],
  templateUrl: './product-update.component.html',
  styleUrl: './product-update.component.css'
})
export class ProductUpdateComponent implements OnInit {
  form!: FormGroup
  product: IProduct | null = null
  files = [];
  totalSize: number = 0;
  totalSizePercent: number = 0;
  selectedImages: File[] = []

  constructor(
    private config: PrimeNG, private spinner: SpinnerOverlayService,
    private fb: FormBuilder, public dialogRef: MatDialogRef<ProductUpdateComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private productService: ProductService,
    private customerService: CustomerService,
    private roleService: RoleService,
    private messageService: MessageService
  ) {
  }

  ngOnInit() {
    this.form = this.fb.group({
      name: [this.product?.name ?? '', [Validators.required]],
      description: [this.product?.description ?? '', [Validators.required]],
      usesAndApplications: [this.product?.usesAndApplications ?? '', [Validators.required]],
      design: [this.product?.design ?? '', [Validators.required]],
      images: [this.product?.images ?? [], [Validators.required]],
      keywords: [this.product?.keywords ?? ''],
    })

    if (this.data) {
      this.findById()
    }
  }

  findById() {
    this.handleLoading(true)

    this.productService.findById(this.data.id).subscribe({
      next: (response) => {
        this.product = response.body?.product ?? null
        this.patchFormProduct()
        this.handleLoading(false)
      },
      error: () => {
        this.handleLoading(false)
      }
    })
  }

  close(save: boolean): void {
    this.dialogRef.close(save)
  }

  get f() {
    return this.form.controls
  }

  get colorArray(): FormArray {
    return this.form.get('color') as FormArray;
  }

  addProductImages(event: any) {
    const files: File[] = Array.from(event.files);

    const control = this.form.get('images');

    const existingFiles: File[] = control?.value ?? [];
    const updatedFiles = [
      ...existingFiles,
      ...files.filter(f => !existingFiles.some(ef => ef.name === f.name))
    ];

    this.form.patchValue({
      images: updatedFiles
    })
    control?.markAsDirty();
    control?.updateValueAndValidity();
  }


  addColorImage(event: any, index: number) {
    const file: File = event.files[0];
    if (!file) return;
    const colorArray = this.form.get('color') as FormArray;
    const colorGroup = colorArray.at(index) as FormGroup;

    colorGroup.patchValue({ image: file });
    colorGroup.get('image')?.markAsDirty();
    colorGroup.get('image')?.markAsTouched();
    colorGroup.updateValueAndValidity();
  }

  removeColorImage(index: number) {
    const colorArray = this.form.get('color') as FormArray;
    const colorGroup = colorArray.at(index) as FormGroup;

    colorGroup.patchValue({ image: null });
    colorGroup.get('image')?.markAsDirty();
    colorGroup.get('image')?.markAsTouched();
    colorGroup.get('image')?.updateValueAndValidity();
  }

  removeAllProductImages() {
    this.form.patchValue({
      images: []
    })
  }

  onRemoveTemplatingFile(event: any, file: {
    size: any
  }, removeFileCallback: (arg0: any, arg1: any) => void, index: any) {
    removeFileCallback(event, index);
    this.totalSize -= parseInt(this.formatSize(file.size));
    this.totalSizePercent = this.totalSize / 10;
    const control = this.form.get('images');
    if (!control) return;

    const updatedFiles = [...(control.value || [])];
    updatedFiles.splice(index, 1);

    control.patchValue(updatedFiles);
    control.markAsDirty();
    control.markAsTouched();
    control.updateValueAndValidity();
  }

  formatSize(bytes: number) {
    const k = 1024;
    const dm = 3;
    const sizes = this.config.translation.fileSizeTypes;
    if (bytes === 0) {
      return `0 ${sizes ? sizes : [0]}`;
    }

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const formattedSize = parseFloat((bytes / Math.pow(k, i)).toFixed(dm));

    return `${formattedSize} ${sizes ? sizes : [i]}`;
  }

  update() {
    this.handleLoading(true);

    const formValue = this.form.value;

    if (formValue.images.length > 5) {
      this.messageService.showError("Se excedió la cantidad de imágenes permitidas para el producto (5)")
      this.handleLoading(false)
      return
    }

    const formData = new FormData();

    try {

      formData.append('name', String(formValue.name ?? ''));
      formData.append('id', String(this.product?.id ?? ''));
      formData.append('description', formValue.description);
      formData.append('usesAndApplications', formValue.usesAndApplications);
      formData.append('design', formValue.design);
      if (formValue.keywords) {
        formData.append('keywords', formValue.keywords);
      }
      formData.append('active', String(this.product?.active ?? false));

      formValue.images.forEach((file: File, i: number) => {
        const hasOriginalUrl = (file as any).originalUrl;

        if (hasOriginalUrl) {
          formData.append("existingImages", (file as any).originalUrl);
        } else {
          formData.append("images", file);
        }
      });
    }
    catch (e) {
      this.messageService.showError("Error al preparar las imágenes del producto: " + e);
      this.handleLoading(false);
      return;
    }

    this.productService.update(formData, this.data.id).subscribe({
      next: () => {
        this.handleLoading(false);
        this.close(true);
      },
      error: () => {
        this.handleLoading(false);
      }
    });
  }

  async patchFormProduct() {
    const files: File[] = [];
    const existingImages = this.product?.images as IImage[]

    if (existingImages?.length) {
      for (const existingImage of existingImages) {
        try {
          const res = await fetch(existingImage.url);
          const blob = await res.blob();
          const fileName = existingImage.url.split('/').pop() || `image-${files.length + 1}.jpg`;
          const file = new File([blob], fileName, { type: blob.type });
          (file as any).originalUrl = existingImage.url;
          files.push(file);
        } catch (e) {
          this.messageService.showError(`Error al cargar la imagen ${existingImage}: ` + e);
        }
      }
    }

    if (!this.product) return;

    this.form.patchValue({
      name: this.product?.name,
      description: this.product?.description,
      usesAndApplications: this.product?.usesAndApplications,
      design: this.product?.design,
      keywords: this.product?.keywords,
      images: files
    });

    this.selectedImages = files;
  }

  handleLoading(loading: boolean) {
    if (loading) this.spinner.show(); else this.spinner.hide()
  }

  choose(event: any, callback: any) {
    callback();
  }
}
